import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { Button } from 'components/styled/button'
import { mapLanguageToComponentProps } from 'i18n'
import VideoDraft from 'components/video-draft/'
import Confirm from 'components/confirm/confirm'
import services from 'services'
import {
  videoUploadInProgress,
  videoUploadFinished,
} from 'actions/video-response'
import { USE_FILE_UPLOAD } from 'features'
import { Flex } from 'components/styled/containers/flex'
import { Body, Subtitle } from 'components/styled/typography'
import VideoPlaceholder from 'components/video-placeholder/video-placeholder'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import { Box } from 'components/styled/containers/box'

const propTypes = {
  video: PropTypes.shape({
    _id: PropTypes.string,
    videoUrl: PropTypes.string,
    has: PropTypes.bool,
    beingProcessed: PropTypes.bool,
    conversionError: PropTypes.bool,
  }),
  context: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  type: PropTypes.string,
  pollId: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    videoUpload: PropTypes.shape({
      error: PropTypes.shape({
        upload: PropTypes.string,
        response: PropTypes.string,
        transcode: PropTypes.string,
        invalid: PropTypes.string,
      }),
      uploading: PropTypes.string,
      remove: PropTypes.string,
      converting: PropTypes.string,
      uploadPlaceholder: PropTypes.func,
      uploadPlaceholderCandidate: PropTypes.string,
      videoPlaceholder: PropTypes.string,
      notAnswered: PropTypes.string,
    }).isRequired,
  }).isRequired,
  updateVideo: PropTypes.func.isRequired,
  className: PropTypes.string,
  onVideoSubmit: PropTypes.func,
  fileUpload: PropTypes.bool,
  globalRecorder: PropTypes.string,
  recordButton: PropTypes.node,
  candidateInfo: PropTypes.shape({}),
  isEmbedded: PropTypes.bool,
  videoUploadEvent: PropTypes.shape({}),
  disableUpload: PropTypes.bool,
}

const defaultProps = {
  video: {},
  type: undefined,
  className: undefined,
  onVideoSubmit: undefined,
  fileUpload: false,
  globalRecorder: '',
  recordButton: null,
  candidateInfo: {},
  isEmbedded: false,
  videoUploadEvent: {},
  disableUpload: false,
}

const UploadProgressContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.space.s20};
`

const ProgressBar = styled.div`
  position: absolute;
  width: 1%;
  height: 100%;
  background-color: ${({ theme }) => theme.color.primary110};
`

const UploadProgress = styled.div`
  position: relative;
  width: 100%;
  height: 30px;
  background-color: ${({ theme }) => theme.color.grey30};
`

const HiddenInput = styled.input`
  border: 0px;
  clip: rect(0px, 0px, 0px, 0px);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0px;
  overflow: hidden;
  white-space: nowrap;
  position: absolute;
`

export class VideoUpload extends Component {
  constructor(props) {
    super(props)

    this.state = {
      progress: null,
      uploading: false,
      invalid: false,
      confirm: null,
    }

    this.onTransitionEnd = this.onTransitionEnd.bind(this)
    this.removeTake = this.removeTake.bind(this)
    this.onChange = this.onChange.bind(this)
  }

  componentDidMount() {
    const { video } = this.props

    if (video.beingProcessed && !video.conversionError && video.has) {
      this.pollVideo()
    }
  }

  componentDidUpdate(prevProps) {
    const { context, video } = this.props
    if (
      typeof video.beingProcessed === 'boolean' &&
      !video.beingProcessed &&
      prevProps.video.beingProcessed
    ) {
      this.removeInterval()
    }

    if (
      context === 'admin' &&
      video.beingProcessed &&
      !prevProps.video.beingProcessed &&
      video.has
    ) {
      this.pollVideo()
    } else if (video._id && !prevProps.video._id) {
      if (video.beingProcessed && !video.conversionError && video.has) {
        this.pollVideo()
      }
    }
  }

  componentWillUnmount() {
    this.removeInterval()
  }

  onTransitionEnd() {
    const { progress } = this.state
    if (progress && progress.width === '100%') {
      this.setState({ progress: null })
    }
  }

  onChange({ target }) {
    this.signUploadUrl(target.files)
  }

  setUploadError(error, files) {
    const { context, video, i18n } = this.props
    this.setState({
      progress: null,
      confirm: {
        message: i18n.videoUpload.error.upload,
        onCancel:
          context === 'admin'
            ? () =>
                this.setState({ confirm: null, uploading: false }, () =>
                  this.removeTake()
                )
            : () => this.setState({ confirm: null, uploading: false }),
        onConfirm: () =>
          this.setState({ confirm: null }, () => this.signUploadUrl(files)),
      },
    })
    if (typeof Raven !== 'undefined') {
      const extra = { video }
      if ('response' in error) {
        extra.response = error.response
      }
      Raven.captureException(error, {
        extra,
      })
    }
  }

  signUploadUrl(files) {
    const { video, context } = this.props
    if (context === 'admin') {
      if (files[0].size > 512000 * 1024) {
        this.setState({ invalid: true })
      } else {
        services
          .xhr('/v1/video/signuploadfile', {
            method: 'POST',
            body: { video: video.videoUrl },
          })
          .then(signedUrl => {
            this.uploadFile(files, signedUrl)
          })
          .catch(error => this.setUploadError(error, files))
      }
    } else {
      services
        .xhr('/v1/video/signuploadfile', {
          method: 'POST',
          body: { video: video.videoUrl },
        })
        .then(signedUrl => {
          this.uploadFile(files, signedUrl)
        })
        .catch(error => this.setUploadError(error, files))
    }
  }

  pollUrl() {
    const { context, pollId, type, video } = this.props
    switch (context) {
      case 'admin':
        return `/v1/admin/videos/uploads/${pollId}`
      case 'messages':
        return `/v1/messages/${type}/${pollId}/${video._id}`
      default:
        return `/v1/${context}/${pollId}/${video._id}`
    }
  }

  pollReq() {
    const { context, video } = this.props
    switch (context) {
      case 'admin':
        return { method: 'POST', body: video }
      default:
        return { method: 'GET' }
    }
  }

  pollVideo() {
    const { context, dispatch, video } = this.props

    if (!this.interval) {
      this.pollTranscodingStatus(() => {
        this.updateVideo({ has: true, beingProcessed: false })
        if (context !== 'admin') {
          dispatch(videoUploadFinished({ videoUrl: video.videoUrl }))
        }
      })
    }
  }

  pollTranscodingStatus(success) {
    this.interval = setInterval(() => {
      const req = this.pollReq()
      services.xhr(this.pollUrl(), req).then(response => {
        if (response.conversionError) {
          this.updateVideo({
            has: true,
            beingProcessed: false,
            conversionError: true,
          })
        } else if (
          typeof response.beingProcessed === 'boolean' &&
          !response.beingProcessed
        ) {
          success()
        }
      })
    }, 4000)
  }

  removeInterval() {
    if (this.interval) {
      clearInterval(this.interval)
      delete this.interval
    }
  }

  removeTake() {
    const { dispatch, onRemove, video } = this.props

    this.removeInterval()
    dispatch(videoUploadFinished({ videoUrl: video.videoUrl }))
    onRemove()
  }

  uploadFile(files, signedUrl) {
    const headers = {
      'Content-Type': 'video/mp4',
    }
    const { dispatch, video } = this.props

    dispatch(videoUploadInProgress({ videoUrl: video.videoUrl }))
    this.setState(
      { progress: { width: 0 }, invalid: false, uploading: true },
      () => {
        services
          .xhr(signedUrl, {
            outsideUrl: true,
            method: 'PUT',
            blob: files[0],
            headers,
            onProgress: event => {
              const { lengthComputable, loaded, total } = event
              const width = `${((loaded / total) * 100).toFixed(0)}%`
              if (lengthComputable) {
                this.setState({ progress: { width } })
              }
            },
          })
          .then(() => {
            this.setState({ uploading: false }, () => {
              this.updateVideo({ has: true, beingProcessed: true })
            })
          })
          .catch(error => {
            this.setUploadError(error, files)
          })
      }
    )
  }

  updateVideo(oldVideo) {
    const { updateVideo } = this.props
    const video = { ...oldVideo, recorder: 'upload' }
    updateVideo(video)
  }

  renderUploadBar(className) {
    const { i18n } = this.props
    const { progress } = this.state

    return (
      <VideoPlaceholder>
        <UploadProgressContainer className={className}>
          <div>
            <UploadProgress>
              <ProgressBar
                style={progress}
                onTransitionEnd={() => this.onTransitionEnd()}
              />
            </UploadProgress>
            <Body mt="s20" textAlign="center">
              {i18n.videoUpload.uploading}
            </Body>
          </div>
        </UploadProgressContainer>
      </VideoPlaceholder>
    )
  }

  renderTranscodingSpinner(className) {
    const { video, i18n } = this.props

    return (
      <VideoPlaceholder>
        <UploadProgressContainer className={className}>
          {video.conversionError ? (
            <div className="upload-error">
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Subtitle>{i18n.videoUpload.error.transcode}</Subtitle>
                <Button
                  icon="delete"
                  variant="secondary"
                  text={i18n.videoUpload.remove}
                  onClick={() => this.removeTake()}
                />
              </Flex>
            </div>
          ) : (
            <div>
              <LoadingIndicator size="s64" />
              <Body mt="s20" textAlign="center">
                {i18n.videoUpload.converting}
              </Body>
            </div>
          )}
        </UploadProgressContainer>
      </VideoPlaceholder>
    )
  }

  render() {
    const {
      context,
      onRemove,
      onVideoSubmit,
      video,
      i18n,
      fileUpload,
      globalRecorder,
      className,
      recordButton,
      isEmbedded,
      videoUploadEvent,
      disableUpload,
    } = this.props
    const capture = fileUpload ? undefined : 'camera'
    const { invalid, confirm, uploading } = this.state

    const customClassName = ['upload', className].join(' ')

    if (confirm) {
      return (
        <Flex
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          style={{ height: '100%' }}
        >
          <div className={customClassName}>
            <Confirm
              message={confirm.message}
              onCancel={confirm.onCancel}
              onConfirm={confirm.onConfirm}
            />
          </div>
        </Flex>
      )
    }

    if (uploading && !confirm) {
      return this.renderUploadBar(customClassName)
    }

    if (video.has && !video.beingProcessed && !video.conversionError) {
      return (
        <VideoDraft
          className={customClassName}
          context={context}
          video={video}
          onRemove={onRemove}
          onVideoSubmit={onVideoSubmit}
          isUpload
          isEmbedded={isEmbedded}
        />
      )
    }

    if (video.has && (video.beingProcessed || video.conversionError)) {
      return this.renderTranscodingSpinner(customClassName)
    }

    const disabled =
      disableUpload ||
      !!(recordButton && recordButton.props && recordButton.props.disabled)

    return (
      <VideoPlaceholder>
        <HiddenInput
          ref={c => {
            this.videoInput = c
          }}
          className="video-upload"
          type="file"
          accept="video/mp4,video/x-m4v,video/*"
          capture={capture}
          onChange={event => this.onChange(event)}
        />
        {invalid ? (
          <Subtitle className="upload-invalid-file">
            {i18n.videoUpload.error.invalid}
          </Subtitle>
        ) : null}
        {context !== 'admin' && (
          <Subtitle mb="s12" className="notanswered">
            {i18n.videoUpload.notAnswered}
          </Subtitle>
        )}
        <Flex flexDirection="column" alignItems="center">
          {recordButton && <Box mb="s16">{recordButton}</Box>}
          {context === 'admin' ? (
            <Button
              text={i18n.videoUpload.uploadPlaceholder(
                USE_FILE_UPLOAD || globalRecorder === 'upload'
              )}
              event={videoUploadEvent}
              icon="file_upload"
              onClick={() => this.videoInput.click()}
            />
          ) : (
            <Button
              bsStyle="primary"
              icon="file_upload"
              event={videoUploadEvent}
              text={
                recordButton
                  ? i18n.videoUpload.uploadPlaceholderCandidate
                  : i18n.videoUpload.videoPlaceholder
              }
              onClick={() => this.videoInput.click()}
              disabled={disabled}
            />
          )}
        </Flex>
      </VideoPlaceholder>
    )
  }
}

VideoUpload.propTypes = propTypes
VideoUpload.defaultProps = defaultProps

export default connect(
  mapLanguageToComponentProps(state => ({
    globalRecorder: state.global.recorder,
  }))
)(VideoUpload)
